import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { sanitizeString } from '../utils/security';

const NotificationStack = () => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef({});

  // Dışarıdan mesaj eklemek için fonksiyon
  const addNotification = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    const safeMessage = sanitizeString(message);
    
    // Önceki timer'ı temizle (eğer varsa)
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
    }
    
    setNotifications((prev) => {
      const newNotifications = [{ id, type, message: safeMessage, timestamp: Date.now() }, ...prev];
      // En fazla 4 mesaj göster
      return newNotifications.slice(0, 4);
    });

    // 5 saniye sonra kaldır
    timersRef.current[id] = setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      delete timersRef.current[id];
    }, 5000);
  }, []);

  // Global erişim için window'a ekle
  useEffect(() => {
    window.showNotification = addNotification;
    return () => {
      delete window.showNotification;
      // Tüm timer'ları temizle
      Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
      timersRef.current = {};
    };
  }, [addNotification]);

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return 'fas fa-check-circle';
      case 'error':
        return 'fas fa-exclamation-circle';
      case 'warning':
        return 'fas fa-exclamation-triangle';
      case 'info':
        return 'fas fa-info-circle';
      default:
        return 'fas fa-info-circle';
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'success':
        return '#10b981'; // Yeşil
      case 'error':
        return '#ef4444'; // Kırmızı
      case 'warning':
        return '#f97316'; // Turuncu-sarı karışımı
      case 'info':
        return '#2563eb'; // Mavi
      default:
        return '#2563eb';
    }
  };

  // Opacity hesaplama: 1. mesaj 100%, 2. 75%, 3. 50%, 4. 25%
  const getOpacity = (index) => {
    switch (index) {
      case 0:
        return 1.0; // 100%
      case 1:
        return 0.75; // 75%
      case 2:
        return 0.5; // 50%
      case 3:
        return 0.25; // 25%
      default:
        return 0.25;
    }
  };

  const stack = (
    <div className="notification-stack" role="region" aria-label="Bildirimler" aria-live="polite">
      {notifications.map((notification, index) => {
        const opacity = getOpacity(index);

        return (
          <div
            key={notification.id}
            className="notification-item"
            data-type={notification.type}
            style={{
              '--notification-color': getColor(notification.type),
              opacity,
            }}
          >
            <div className="notification-content">
              <i
                className={getIcon(notification.type)}
                style={{ color: getColor(notification.type) }}
                aria-hidden
              />
              <span className="notification-message">{notification.message}</span>
            </div>
            <button
              type="button"
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
              aria-label="Kapat"
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </div>
        );
      })}
    </div>
  );

  if (typeof document === 'undefined') return stack;
  return createPortal(stack, document.body);
};

export default NotificationStack;

